// @ts-strict-ignore
import {
  Box, Checkbox, Icon, IconName, MultiSelect, Text, TextField, Token, Tooltip,
} from '@usealloy/component-library';
import React, { useContext, useMemo } from 'react';

import { Controller, useFormContext } from 'react-hook-form';
import capitalize from 'lodash/capitalize';
import {
  StyledAttributeFormContainer,
  StyledLeftColumnContainer,
  StyledPublishedAttributeBanner,
  StyledRightColumnContainer,
} from '../styles';

import type { FormState } from './types';
import { getDataTypeOptions, getDefineValueRangesHelpText } from './helpers';
import { ReadOnlyValueRangeConfig } from '~/components/ValueRangeConfig';
import AcceptedValuesField from '~/dashboard/views/settings/published-attributes/components/AcceptedValuesField';
import ValidValuesField from '~/dashboard/views/settings/published-attributes/components/ValidValuesField';
import {
  outputCategoryOptions,
  PII_CHECKBOX_LABEL,
  PII_CHECKBOX_HELP_TEXT,
} from '~/dashboard/views/settings/published-attributes/constants';
import { UserContext } from '~/dashboard/context';
import DefaultValueField from '../DefaultValueField';
import { getAgentUpdateValueTooltipText } from '~/dashboard/views/settings/published-attributes/components/AddPublishedAttributeModal';
import { usePublishedAttributeGroupingsList } from '~/dashboard/views/settings/published-attributes/hooks';
import type { Option } from '~/journey/DeveloperGuide/types';
import type { NodeEditorAutoSaveTabProps } from '../../useNodeEditorForm/types';
import useNodeFormAutoSave from '../../useNodeFormAutoSave';
import usePreventEmptyRegister from '../../usePreventEmptyRegister';
import {
  selectActions, selectDirectDependentActions, selectFocusedAction, selectWorkflowProps,
} from 'flow/store/redux/selectors';
import { useAppSelector } from '~/store/reduxHooks';
import ConfirmDataTypeChangeModal from '../Footer/FooterModals/ConfirmDataTypeChangeModal';
import { publishedAttributeFormFields } from '../types';
import { ActionType, OUTPUT_CATEGORY_RISK_SCORING, OUTPUT_CATEGORY_SESSION_RISK_SCORING } from 'flow/store/engine/constants';
import { getExistingNamesForActionType, getPublishedDetails, isActionDataObjectEphemeral } from 'flow/service/actions';
import { isAttributeNameUnique, validationMessages } from '../validation';
import { useEditorAccess } from 'flow/editor/context';
import { getIsEditable } from 'flow/service/general';

export default function OutputOptionsTab({
  handleAutoSave, isReadOnly = false,
}: NodeEditorAutoSaveTabProps) {
  const focusedAction = useAppSelector(selectFocusedAction);
  const workflowProps = useAppSelector(selectWorkflowProps);
  const actions = useAppSelector(selectActions);
  const { editorMode } = useEditorAccess();
  const inEditMode = getIsEditable(editorMode) && !isReadOnly;
  const dataTypeOptions = useMemo(() => getDataTypeOptions(), []);
  const { featureFlags, permissions } = useContext(UserContext);
  const agentCanUpdatePublishedAttributes = permissions['UPDATE:PublishedAttributes:null'];

  const existingOutputNames = useMemo(
    () => getExistingNamesForActionType(actions, ActionType.OUTPUT, focusedAction.publicId),
    [actions, focusedAction.publicId],
  );

  const focusedActionDependents = useAppSelector(selectDirectDependentActions);
  const hasDependents = focusedActionDependents.length > 0;

  const {
    register,
    control,
    watch,
    formState,
    setValue,
    handleSubmit,
    getFieldState,
  } = useFormContext<FormState>();

  const {
    data: attributeGroupings,
    isLoading: attributeGroupingsLoading,
  } = usePublishedAttributeGroupingsList();

  const groupingOptions = useMemo(() => (
    (attributeGroupings || [])
      .filter(attribute => !attribute.archived)
      .map(({ groupingName, groupingToken }) => ({
        label: groupingName,
        value: groupingToken,
      }))
  ), [attributeGroupings]);

  const dataType = watch('dataType');
  const hasExpiration = watch('hasExpiration');
  const publishedAttributeToken = watch('attributeToken');
  const outputCategory = watch('outputCategory');
  const defineValueRanges = watch('defineValueRanges');
  const outputValueRange = watch('outputValueRange');
  const outputValueRangeOptions = watch('outputValueRangeOptions');

  const isPublishedAttribute = Boolean(publishedAttributeToken);
  const {
    paValidationImprovements,
  } = featureFlags;

  // Get the objectType from the published attribute
  const publishedAttribute = workflowProps?.publishedAttributes?.find(
    pa => pa.attributeToken === publishedAttributeToken,
  );
  const objectType = publishedAttribute?.objectType;

  // outputValueRange is configured on the PA and is read-only here
  // outputValueRange will be sent as an Action Option, but will copy the value_range_config from the PA
  const showReadOnlyValueRangeConfig = defineValueRanges
    && outputValueRange;

  useNodeFormAutoSave<FormState>({
    fn: handleAutoSave,
    // do not autosave published attribute data
    ignoreFields: isPublishedAttribute
      ? [...publishedAttributeFormFields, 'dataType']
      : ['dataType'],
  });

  const isDataTypeChangeConfirmationModalOpen = dataType && hasDependents
    && focusedAction.output.dataType !== dataType?.value;

  const preventEmptyRegister = usePreventEmptyRegister<FormState>();

  return (
    <StyledAttributeFormContainer>
      {isDataTypeChangeConfirmationModalOpen && (
        <ConfirmDataTypeChangeModal
          isOpen
          focusedAction={focusedAction}
          focusedActionDependents={focusedActionDependents}
          onSave={data => handleAutoSave(data)}
        />
      )}
      <StyledLeftColumnContainer>
        {isPublishedAttribute ? (
          <Controller
            control={control}
            name="attributeName"
            rules={{
              required: {
                value: true,
                message: validationMessages.attributeName.required,
              },
              validate: {
                uniqueName: (value: string) => {
                  const fieldState = getFieldState('attributeName', formState);
                  if (!fieldState.isDirty) return true;
                  const originalAttributeName = getPublishedDetails(focusedAction)?.attributeName
                    || null;
                  return (
                    isAttributeNameUnique(
                      workflowProps?.publishedAttributes || [],
                      value,
                      originalAttributeName,
                    )
                || validationMessages.attributeName.uniqueName
                  );
                },
              },
            }}
            render={({
              field: {
                onChange, onBlur, value, ref,
              },
            }) => (
              <TextField
                ref={ref}
                error={formState.errors?.attributeName?.message}
                label="Display Name"
                type="text"
                disabled={
              !inEditMode || (isPublishedAttribute && !agentCanUpdatePublishedAttributes)
            }
                onChange={onChange}
                onBlur={onBlur}
                value={value}
              />
            )}
          />
        ) : (
          <TextField
            label="Display Name"
            type="text"
            {...preventEmptyRegister('attributeName', {
              validate: value => (
                existingOutputNames.has(value)
                  ? 'An output attribute with this name already exists in this workflow version.'
                  : true
              ),
            })}
            disabled={!inEditMode}
            error={inEditMode && formState.errors?.attributeName?.message}
          />
        )}

        <Controller
          name="dataType"
          control={control}
          rules={{
            deps: isPublishedAttribute && paValidationImprovements
              ? ['validValueMax', 'validValueMin']
              : [],
          }}
          render={({ field: { onChange, value } }) => (
            <MultiSelect
              isMulti={false}
              label="Data Type"
              isDisabled={!inEditMode || isPublishedAttribute}
              onChange={(newValue: Option) => {
                onChange(newValue);
                if (!hasDependents) {
                  handleSubmit(handleAutoSave)();
                }
              }}
              options={dataTypeOptions}
              selectedOptions={typeof value === 'string' ? { value, label: capitalize(value) } : value}
            />
          )}
        />
        {isPublishedAttribute && (
          <>
            {featureFlags?.groupedPublishedAttributes && (
              <Controller
                control={control}
                name="groupingToken"
                render={({ field: { onChange, value } }) => (
                  <MultiSelect
                    label="Grouping"
                    isMulti={false}
                    isSearchable
                    onChange={(newValue: Option) => {
                      onChange(newValue.value);
                    }}
                    selectedOptions={groupingOptions.find(option => option.value === value) || null}
                    options={groupingOptions}
                    isDisabled={isReadOnly || attributeGroupingsLoading || !agentCanUpdatePublishedAttributes || objectType === 'transaction'}
                    placeholder={
                      attributeGroupingsLoading
                        ? 'Published Attribute groupings loading...'
                        : 'Select a grouping to add your published attribute to'
                    }
                  />
                )}
              />
            )}
            <Controller
              control={control}
              name="outputCategory"
              render={({ field: { onChange, value } }) => (
                <MultiSelect
                  label="Category"
                  isClearable
                  isMulti={false}
                  options={outputCategoryOptions}
                  selectedOptions={
                      outputCategoryOptions.find(option => option.value === value) ?? null
                    }
                  onChange={(newValue: Option) => {
                    onChange(newValue?.value ?? '');
                    if (newValue?.value === OUTPUT_CATEGORY_RISK_SCORING
                      || newValue?.value === OUTPUT_CATEGORY_SESSION_RISK_SCORING) {
                      setValue('defineValueRanges', true);
                    }
                  }}
                    // category is set on the PA and is read-only here
                    // Category will be sent as an Action Option, but will copy the value from the PA
                  isDisabled
                  helpText={!outputCategory && 'Categories can only be set in Published Attribute settings.'}
                />
              )}
            />
            <DefaultValueField
              dataType={dataType?.value}
              control={control}
              register={register}
              errors={inEditMode && formState.errors}
              setValue={setValue}
              disabled={!inEditMode || !agentCanUpdatePublishedAttributes}
            />
            <TextField
              label="Default Value Note"
              type="text"
              {...register('defaultValueNote')}
              disabled={!inEditMode || !agentCanUpdatePublishedAttributes}
            />
            {paValidationImprovements && dataType?.value === 'string' && (
              <ValidValuesField
                control={control}
                errors={formState.errors}
                disabled={!inEditMode || !agentCanUpdatePublishedAttributes}
              />
            )}
            {paValidationImprovements && (
              <AcceptedValuesField
                control={control}
                dataType={dataType?.value}
                errors={formState.errors}
                disabled={!inEditMode || !agentCanUpdatePublishedAttributes}
              />
            )}
          </>
        )}
      </StyledLeftColumnContainer>
      <StyledRightColumnContainer
        isPublishedAttribute={isPublishedAttribute}
      >
        {isPublishedAttribute && (
          <>
            <Box justifyContent="flex-end">
              {!isActionDataObjectEphemeral(focusedAction)
                && (
                <Token
                  style={{ marginBottom: 'calc(-1 * var(--cl-space-5))' }}
                  displayContent={publishedAttributeToken}
                  copyContent={publishedAttributeToken}
                />
                )}
            </Box>
            <Controller
              control={control}
              name="containsPii"
              render={({ field: { onChange, value } }) => (
                <Checkbox
                  checked={value}
                  disabled
                  label={PII_CHECKBOX_LABEL}
                  onChange={onChange}
                  helpText={PII_CHECKBOX_HELP_TEXT}
                />
              )}
            />
            <Controller
              control={control}
              name="hasExpiration"
              render={({ field: { onChange, value } }) => (
                <Checkbox
                  onChange={onChange}
                  checked={value}
                  label="This attribute expires"
                  disabled={!inEditMode || !agentCanUpdatePublishedAttributes}
                />
              )}
            />
            {hasExpiration && (
              <TextField
                type="number"
                label="Days Until Expiration"
                required
                {...register('valueExpiresInDays', {
                  valueAsNumber: true,
                })}
                disabled={!inEditMode || !agentCanUpdatePublishedAttributes}
              />
            )}
            {paValidationImprovements && (
              <Controller
                control={control}
                name="agentCanCreateValue"
                render={({ field: { onChange, value } }) => (
                  <Box justifyContent="flex-start">
                    <Checkbox
                      onChange={onChange}
                      checked={value}
                      label="Agents can update this attribute's values in the dashboard"
                      disabled={!inEditMode || !agentCanUpdatePublishedAttributes}
                    />
                    <Tooltip
                      side="top"
                      content={getAgentUpdateValueTooltipText(objectType)}
                    >
                      <Icon icon={IconName.Info} />
                    </Tooltip>
                  </Box>
                )}
              />
            )}
            <Controller
              control={control}
              name="hasJqFilter"
              data-dd-action-name="output-jq-checkbox"
              render={({ field: { onChange, value } }) => (
                <Checkbox
                  checked={value}
                  onChange={onChange}
                  label="Parse with JQ?"
                  disabled
                />
              )}
            />
            <StyledPublishedAttributeBanner>
              <Icon icon={IconName.Exclamation} />
              <Text>
                JQ here is only relevant for the input attribute version of this Published Attribute
              </Text>
            </StyledPublishedAttributeBanner>
            {isPublishedAttribute && (
              <Controller
                control={control}
                name="defineValueRanges"
                data-dd-action-name="output-define-value-ranges-checkbox"
                render={({ field: { onChange, value } }) => (
                  <Checkbox
                    checked={value}
                    onChange={onChange}
                    label="Define ranges for values"
                    disabled
                    helpText={getDefineValueRangesHelpText({
                      outputValueRange,
                    })}
                  />
                )}
              />
            )}
            {
              showReadOnlyValueRangeConfig && (
                <ReadOnlyValueRangeConfig
                  outputValueRange={outputValueRange}
                  outputValueRangeOptions={outputValueRangeOptions}
                />
              )
            }
          </>
        )}
      </StyledRightColumnContainer>
    </StyledAttributeFormContainer>
  );
}

